import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Calendar,
  MapPin,
  Clock,
  Users,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  Send,
  ExternalLink,
  ChevronLeft,
  Ticket,
  Crown,
  CheckCircle2,
  XCircle,
  LogOut,
  Sparkles,
  Image as ImageIcon,
  X,
  Star,
  Zap,
  PartyPopper,
  CalendarDays,
  ArrowRight,
  Gift,
  Target,
  TrendingUp,
  Gem,
  Lock,
  Copy
} from 'lucide-react';
import { isNativePlatform, hasFullAccess } from '../lib/platform';

// Backend-served images are relative (/api/...); prefix so they also load in the native app
const resolveImageUrl = (url) =>
  url && url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const EventsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Check if user is a club member (can register for events)
  const isClubMember = hasFullAccess(user?.subscription_tier);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventsRes, organizerRes, registrationsRes] = await Promise.all([
        axios.get(`${API}/events/?include_drafts=true`, { withCredentials: true }),
        axios.get(`${API}/events/check-organizer`, { withCredentials: true }),
        axios.get(`${API}/events/user/my-registrations`, { withCredentials: true })
      ]);
      
      setEvents(eventsRes.data);
      setIsOrganizer(organizerRes.data.is_organizer);
      setMyRegistrations(registrationsRes.data.map(e => e.event_id));
      
      if (organizerRes.data.is_organizer) {
        const myEventsRes = await axios.get(`${API}/events/my-events`, { withCredentials: true });
        setMyEvents(myEventsRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des événements');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleRegister = async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/register`, {}, { withCredentials: true });
      toast.success('Inscription réussie !');
      setMyRegistrations([...myRegistrations, eventId]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    }
  };

  const handleUnregister = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/unregister`, { withCredentials: true });
      toast.success('Désinscription effectuée');
      setMyRegistrations(myRegistrations.filter(id => id !== eventId));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la désinscription');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      toast.success('Événement supprimé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDuplicateEvent = async (eventId) => {
    try {
      const res = await axios.post(`${API}/events/${eventId}/duplicate`, {}, { withCredentials: true });
      toast.success('Événement dupliqué en brouillon — modifiez la date puis enregistrez');
      setMyEvents(prev => [res.data, ...prev]);
      setEditingEvent(res.data);
      setShowCreateModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la duplication');
    }
  };

  const handleNotify = async (eventId) => {
    try {
      const res = await axios.post(`${API}/events/${eventId}/notify`, {}, { withCredentials: true });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handleTogglePublish = async (event) => {
    try {
      await axios.put(`${API}/events/${event.event_id}`, {
        is_published: !event.is_published
      }, { withCredentials: true });
      toast.success(event.is_published ? 'Événement dépublié' : 'Événement publié !');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getShortDate = (dateStr) => {
    if (!dateStr) return { day: '', month: '' };
    const date = new Date(dateStr);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()
    };
  };

  const publishedEvents = events.filter(e => e.is_published);
  const upcomingEvents = publishedEvents.filter(e => new Date(e.date) >= new Date());
  const pastEvents = publishedEvents.filter(e => new Date(e.date) < new Date());

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-950 via-slate-900 to-indigo-950">
      {/* Ambient background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-[600px] bg-gradient-to-b from-blue-500/5 to-transparent" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute top-40 left-10 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-violet-500/5 rounded-full blur-3xl" />
      </div>

      {/* Header - Dark Premium Style */}
      <header className="relative bg-indigo-950 border-b border-indigo-800 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-blue-400 hover:bg-indigo-800/50">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Tableau de bord
                </Button>
              </Link>
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/profile" className="hidden md:flex items-center space-x-2 px-3 py-2 rounded-xl hover:bg-indigo-800/50 transition-colors">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm font-medium shadow-lg shadow-blue-500/20">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-medium text-slate-300">{user?.name}</span>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="hidden md:flex text-slate-500 hover:text-red-400 hover:bg-indigo-800/50">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Premium Events */}
      <div className="relative py-8 sm:py-16 lg:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-8 sm:gap-12">
            {/* Left side - Title & Info */}
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-4 sm:mb-6">
                <PartyPopper className="w-4 h-4 text-blue-400" />
                <span className="text-blue-400 text-xs sm:text-sm font-medium">Événements Exclusifs</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-white mb-3 sm:mb-4 tracking-tight">
                <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-violet-500 bg-clip-text text-transparent">
                  CLUB
                </span>
                <br />
                <span className="text-white">EVENTS</span>
              </h1>
              
              <p className="text-base sm:text-lg lg:text-xl text-slate-400 mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0">
                Networking, masterclass, soirées VIP... Vivez des moments uniques 
                avec la communauté des investisseurs 10M Investor.
              </p>

              {/* Stats */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-6 mb-6 sm:mb-8">
                <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 bg-indigo-900/50 rounded-xl border border-indigo-700/50">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="text-lg sm:text-2xl font-bold text-white">{upcomingEvents.length}</div>
                    <div className="text-[10px] sm:text-xs text-slate-500">À venir</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 bg-indigo-900/50 rounded-xl border border-indigo-700/50">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                  </div>
                  <div>
                    <div className="text-lg sm:text-2xl font-bold text-white">{myRegistrations.length}</div>
                    <div className="text-[10px] sm:text-xs text-slate-500">Inscriptions</div>
                  </div>
                </div>
                {isOrganizer && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-indigo-900/50 rounded-xl border border-indigo-700/50">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <Crown className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white">{myEvents.length}</div>
                      <div className="text-xs text-slate-500">Mes events</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Create Event Button for Organizers */}
              {isOrganizer && (
                <Button
                  onClick={() => { setEditingEvent(null); setShowCreateModal(true); }}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25 px-6 py-3"
                  data-testid="create-event-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Créer un événement
                </Button>
              )}
            </div>

            {/* Right side - Visual Element */}
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-indigo-500/30 rounded-full blur-3xl" />
              <div className="relative w-72 h-72">
                {/* Outer rings */}
                <div className="absolute inset-0 rounded-full border-2 border-blue-500/20 animate-[spin_20s_linear_infinite]" />
                <div className="absolute inset-4 rounded-full border border-indigo-500/30 animate-[spin_15s_linear_infinite_reverse]" />
                
                {/* Center calendar */}
                <div className="absolute inset-10 bg-gradient-to-br from-indigo-900 to-slate-900 rounded-3xl flex flex-col items-center justify-center border border-indigo-700 shadow-2xl shadow-black/50">
                  <div className="text-blue-400 text-sm font-bold mb-1">PROCHAIN</div>
                  {upcomingEvents.length > 0 ? (
                    <>
                      <div className="text-5xl font-black text-white">{getShortDate(upcomingEvents[0].date).day}</div>
                      <div className="text-blue-400 font-bold">{getShortDate(upcomingEvents[0].date).month}</div>
                    </>
                  ) : (
                    <Calendar className="w-16 h-16 text-blue-500/50" />
                  )}
                </div>

                {/* Floating badges */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-indigo-900 rounded-full border border-indigo-700 shadow-lg">
                  <span className="text-xs font-medium text-blue-400">VIP</span>
                </div>
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-indigo-900 rounded-full border border-indigo-700 shadow-lg flex items-center gap-1">
                  <Star className="w-3 h-3 text-amber-400" />
                  <span className="text-xs font-medium text-slate-400">EXCLUSIF</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Banner for free users */}
      {!isClubMember && (
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="relative overflow-hidden">
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/30 via-orange-500/30 to-amber-500/30 rounded-2xl blur-xl" />
            <div className="relative bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30 flex-shrink-0">
                  <Crown className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-xl font-bold text-white mb-2">
                    Rejoignez le Club pour participer aux événements
                  </h3>
                  <p className="text-slate-400 text-sm">
                    Les membres du Club 10M Investor peuvent s'inscrire aux événements exclusifs, 
                    rencontrer des investisseurs et accéder au réseau de professionnels.
                  </p>
                </div>
                {!isNativePlatform() ? (
                <Link to="/pricing">
                  <Button className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-amber-500/25 px-6 py-3 whitespace-nowrap" data-testid="join-club-cta">
                    <Crown className="w-5 h-5 mr-2" />
                    Rejoindre le Club
                  </Button>
                </Link>
                ) : (
                  <p className="text-slate-400 text-sm font-medium whitespace-nowrap">Inscriptions bientôt disponibles</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs for organizers */}
      {isOrganizer && (
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="flex gap-3 p-1.5 bg-indigo-900/50 rounded-xl w-fit border border-indigo-700/50">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                activeTab === 'all' 
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg' 
                  : 'text-slate-400 hover:text-white hover:bg-indigo-800/50'
              }`}
              data-testid="tab-all-events"
            >
              <Calendar className="w-4 h-4" />
              Tous les événements
            </button>
            <button
              onClick={() => setActiveTab('my')}
              className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                activeTab === 'my' 
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg' 
                  : 'text-slate-400 hover:text-white hover:bg-indigo-800/50'
              }`}
              data-testid="tab-my-events"
            >
              <Crown className="w-4 h-4" />
              Mes événements ({myEvents.length})
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="relative">
              <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-xl animate-pulse" />
              <div className="relative w-16 h-16 border-4 border-slate-700 border-t-orange-500 rounded-full animate-spin" />
            </div>
          </div>
        ) : (
          <>
            {/* My Events Section (for organizers) */}
            {isOrganizer && activeTab === 'my' && (
              <div className="space-y-6">
                {myEvents.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="relative inline-block">
                      <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-2xl" />
                      <div className="relative w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center border border-slate-700 mx-auto mb-6">
                        <Calendar className="w-12 h-12 text-slate-600" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Aucun événement créé</h3>
                    <p className="text-slate-400 mb-8 max-w-md mx-auto">
                      Commencez par créer votre premier événement pour la communauté 10M Investor
                    </p>
                    <Button 
                      onClick={() => setShowCreateModal(true)} 
                      className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 shadow-lg shadow-orange-500/25"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Créer un événement
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {myEvents.map(event => (
                      <OrganizerEventCard
                        key={event.event_id}
                        event={event}
                        onEdit={() => { setEditingEvent(event); setShowCreateModal(true); }}
                        onDelete={() => handleDeleteEvent(event.event_id)}
                        onDuplicate={() => handleDuplicateEvent(event.event_id)}
                        onTogglePublish={() => handleTogglePublish(event)}
                        onNotify={() => handleNotify(event.event_id)}
                        onViewRegistrations={() => setSelectedEvent(event)}
                        getShortDate={getShortDate}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* All Events Section */}
            {activeTab === 'all' && (
              <div className="space-y-12">
                {/* Upcoming Events */}
                {upcomingEvents.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                        <Zap className="w-5 h-5 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-white">Événements à venir</h2>
                      <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">
                        {upcomingEvents.length} événement{upcomingEvents.length > 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {upcomingEvents.map(event => (
                        <EventCard
                          key={event.event_id}
                          event={event}
                          isRegistered={myRegistrations.includes(event.event_id)}
                          onRegister={() => handleRegister(event.event_id)}
                          onUnregister={() => handleUnregister(event.event_id)}
                          formatDate={formatDate}
                          getShortDate={getShortDate}
                          isClubMember={isClubMember}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {publishedEvents.length === 0 && (
                  <div className="text-center py-20">
                    <div className="relative inline-block">
                      <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-2xl" />
                      <div className="relative w-24 h-24 bg-indigo-900 rounded-full flex items-center justify-center border border-indigo-700 mx-auto mb-6">
                        <CalendarDays className="w-12 h-12 text-indigo-600" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Aucun événement à venir</h3>
                    <p className="text-slate-400 max-w-md mx-auto">
                      Revenez bientôt pour découvrir les prochains événements exclusifs du Club 10M Investor
                    </p>
                  </div>
                )}

                {/* Past Events */}
                {pastEvents.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-indigo-900 rounded-xl flex items-center justify-center border border-indigo-700">
                        <Clock className="w-5 h-5 text-slate-500" />
                      </div>
                      <h2 className="text-xl font-bold text-slate-400">Événements passés</h2>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 opacity-60">
                      {pastEvents.slice(0, 4).map(event => (
                        <EventCard
                          key={event.event_id}
                          event={event}
                          isRegistered={myRegistrations.includes(event.event_id)}
                          onRegister={() => {}}
                          onUnregister={() => {}}
                          formatDate={formatDate}
                          getShortDate={getShortDate}
                          isPast={true}
                          isClubMember={isClubMember}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Event Modal */}
      {showCreateModal && (
        <EventFormModal
          event={editingEvent}
          onClose={() => { setShowCreateModal(false); setEditingEvent(null); }}
          onSave={() => { setShowCreateModal(false); setEditingEvent(null); fetchData(); }}
        />
      )}

      {/* View Registrations Modal */}
      {selectedEvent && (
        <RegistrationsModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </div>
  );
};

// Event Card Component - Premium Dark Style
const EventCard = ({ event, isRegistered, onRegister, onUnregister, formatDate, getShortDate, isPast = false, isClubMember = true }) => {
  const [recentRegistrations, setRecentRegistrations] = useState([]);
  const spotsLeft = event.max_participants - event.current_participants;
  const isFull = spotsLeft <= 0;
  const dateInfo = getShortDate(event.date);

  useEffect(() => {
    // Fetch recent registrations
    const fetchRecent = async () => {
      try {
        const res = await axios.get(`${API}/events/${event.event_id}/recent-registrations`, { withCredentials: true });
        setRecentRegistrations(res.data);
      } catch (error) {
        console.log('Could not fetch recent registrations');
      }
    };
    if (event.current_participants > 0) {
      fetchRecent();
    }
  }, [event.event_id, event.current_participants]);

  return (
    <Card 
      className="group relative bg-indigo-950/80 backdrop-blur-xl border-indigo-800/50 overflow-hidden hover:border-blue-500/30 transition-all duration-300 rounded-2xl" 
      data-testid={`event-card-${event.event_id}`}
    >
      {/* Glow effect on hover */}
      <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative">
        {/* Image Header */}
        <div className="relative h-48 overflow-hidden">
          {event.image_url ? (
            <img 
              src={resolveImageUrl(event.image_url)} 
              alt={event.title} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-600/20 to-indigo-600/20 flex items-center justify-center">
              <Calendar className="w-20 h-20 text-blue-500/30" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-indigo-950 via-indigo-950/50 to-transparent" />
          
          {/* Date Badge */}
          <div className="absolute top-4 left-4">
            <div className="w-16 h-20 bg-indigo-950/90 backdrop-blur-sm rounded-xl border border-indigo-700 flex flex-col items-center justify-center shadow-xl">
              <span className="text-xs font-bold text-blue-400">{dateInfo.month}</span>
              <span className="text-3xl font-black text-white leading-none">{dateInfo.day}</span>
            </div>
          </div>
          
          {/* Status Badges */}
          <div className="absolute top-4 right-4 flex flex-col gap-2">
            {event.is_free ? (
              <Badge className="bg-emerald-500/90 text-white border-0 shadow-lg">
                <Gift className="w-3 h-3 mr-1" />
                Gratuit
              </Badge>
            ) : (
              <Badge className="bg-amber-500/90 text-white border-0 shadow-lg">
                <Gem className="w-3 h-3 mr-1" />
                Premium
              </Badge>
            )}
            {event.organizer_type === 'founder' && (
              <Badge className="bg-purple-500/90 text-white border-0 shadow-lg">
                <Crown className="w-3 h-3 mr-1" />
                Fondateur
              </Badge>
            )}
          </div>
          
          {/* Title overlay */}
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="text-xl font-bold text-white line-clamp-2 drop-shadow-lg">{event.title}</h3>
          </div>
        </div>

        <CardContent className="p-6 space-y-4">
          {/* Organizer Info */}
          <div className="flex items-center gap-3 p-3 bg-indigo-900/50 rounded-xl border border-indigo-800/50">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-lg shadow-blue-500/20">
              {event.organizer_name?.charAt(0).toUpperCase() || 'O'}
            </div>
            <div>
              <p className="text-xs text-slate-500">Organisé par</p>
              <p className="font-medium text-white">{event.organizer_name || 'Organisateur'}</p>
            </div>
          </div>

          {/* Time */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="font-medium text-white">{formatDate(event.date)}</p>
              <p className="text-sm text-slate-500">
                {event.time_start}{event.time_end && ` - ${event.time_end}`}
              </p>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-500/10 rounded-xl flex items-center justify-center">
              <MapPin className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <p className="font-medium text-white">{event.location}</p>
              <p className="text-sm text-slate-500">{event.address}</p>
            </div>
          </div>

          {/* Description */}
          <p className="text-slate-400 text-sm line-clamp-2">{event.description}</p>

          {/* Spots & Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-400">
                  {event.current_participants}/{event.max_participants} inscrits
                </span>
              </div>
              {spotsLeft > 0 && spotsLeft <= 10 && !isPast && (
                <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30 animate-pulse">
                  Plus que {spotsLeft} places !
                </Badge>
              )}
            </div>
            <div className="w-full h-2 bg-indigo-900 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500"
                style={{ width: `${(event.current_participants / event.max_participants) * 100}%` }}
              />
            </div>
          </div>

          {/* Recent Registrations */}
          {recentRegistrations.length > 0 && (
            <div className="p-3 bg-indigo-900/30 rounded-xl border border-indigo-800/30">
              <p className="text-xs text-slate-500 mb-2">Derniers inscrits</p>
              <div className="flex flex-wrap gap-2">
                {recentRegistrations.map((reg, index) => (
                  <div key={index} className="flex items-center gap-1.5 px-2.5 py-1 bg-indigo-800/50 rounded-full">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold">
                      {reg.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <span className="text-xs text-slate-300">{reg.name?.split(' ')[0] || 'Membre'}</span>
                  </div>
                ))}
                {event.current_participants > 5 && (
                  <div className="flex items-center gap-1 px-2.5 py-1 bg-indigo-800/30 rounded-full">
                    <span className="text-xs text-slate-500">+{event.current_participants - 5} autres</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          {!isPast && (
            <div className="flex gap-3 pt-2">
              {!isClubMember ? (
                // Show "Join Club" button for free users
                isNativePlatform() ? (
                  <div className="flex-1 text-center py-2">
                    <p className="text-sm text-slate-400 font-medium">Inscriptions bientôt disponibles</p>
                  </div>
                ) : (
                <Link to="/pricing" className="flex-1">
                  <Button
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-amber-500/20"
                    data-testid={`join-club-btn-${event.event_id}`}
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Rejoindre le Club
                  </Button>
                </Link>
                )
              ) : isRegistered ? (
                <Button
                  onClick={onUnregister}
                  className="flex-1 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30"
                  data-testid={`unregister-btn-${event.event_id}`}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Inscrit
                </Button>
              ) : isFull ? (
                <Button disabled className="flex-1 bg-indigo-900 text-slate-500 border border-indigo-800">
                  <XCircle className="w-4 h-4 mr-2" />
                  Complet
                </Button>
              ) : (
                <Button
                  onClick={onRegister}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/20"
                  data-testid={`register-btn-${event.event_id}`}
                >
                  <Ticket className="w-4 h-4 mr-2" />
                  S'inscrire
                </Button>
              )}
              {!event.is_free && event.payment_link && (
                <a href={event.payment_link} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              )}
            </div>
          )}
        </CardContent>
      </div>
    </Card>
  );
};

// Organizer Event Card Component - Dark Style
const OrganizerEventCard = ({ event, onEdit, onDelete, onDuplicate, onTogglePublish, onNotify, onViewRegistrations, getShortDate }) => {
  const dateInfo = getShortDate(event.date);
  
  return (
    <Card 
      className={`relative bg-slate-900/80 backdrop-blur-xl border overflow-hidden rounded-2xl ${
        event.is_published ? 'border-emerald-500/30' : 'border-slate-700'
      }`} 
      data-testid={`organizer-event-${event.event_id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start gap-4 mb-4">
          {/* Date Badge */}
          <div className="w-14 h-18 bg-slate-800 rounded-xl border border-slate-700 flex flex-col items-center justify-center flex-shrink-0">
            <span className="text-[10px] font-bold text-orange-400">{dateInfo.month}</span>
            <span className="text-2xl font-black text-white leading-none">{dateInfo.day}</span>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {event.is_published ? (
                <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  <Eye className="w-3 h-3 mr-1" />
                  Publié
                </Badge>
              ) : (
                <Badge className="bg-slate-700 text-slate-400 border border-slate-600">
                  <EyeOff className="w-3 h-3 mr-1" />
                  Brouillon
                </Badge>
              )}
              {event.is_free ? (
                <Badge className="bg-emerald-500/20 text-emerald-400 border-0">Gratuit</Badge>
              ) : (
                <Badge className="bg-amber-500/20 text-amber-400 border-0">Payant</Badge>
              )}
            </div>
            <h3 className="text-lg font-bold text-white truncate">{event.title}</h3>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Clock className="w-4 h-4 text-orange-400" />
            {event.time_start}{event.time_end && ` - ${event.time_end}`}
          </div>
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <MapPin className="w-4 h-4 text-blue-400" />
            {event.location}
          </div>
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Users className="w-4 h-4 text-slate-500" />
            {event.current_participants}/{event.max_participants} inscrits
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button 
            size="sm" 
            onClick={onEdit} 
            className="bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700"
            data-testid={`edit-btn-${event.event_id}`}
          >
            <Edit2 className="w-4 h-4 mr-1" />
            Modifier
          </Button>
          <Button 
            size="sm" 
            onClick={onDuplicate} 
            className="bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700"
            data-testid={`duplicate-btn-${event.event_id}`}
          >
            <Copy className="w-4 h-4 mr-1" />
            Dupliquer
          </Button>
          <Button 
            size="sm" 
            onClick={onTogglePublish} 
            className="bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700"
            data-testid={`publish-btn-${event.event_id}`}
          >
            {event.is_published ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {event.is_published ? 'Dépublier' : 'Publier'}
          </Button>
          {event.is_published && (
            <Button 
              size="sm" 
              onClick={onNotify} 
              className="bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/30"
              data-testid={`notify-btn-${event.event_id}`}
            >
              <Send className="w-4 h-4 mr-1" />
              Notifier
            </Button>
          )}
          <Button 
            size="sm" 
            onClick={onViewRegistrations} 
            className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/30"
            data-testid={`view-registrations-${event.event_id}`}
          >
            <Users className="w-4 h-4 mr-1" />
            Inscrits
          </Button>
          <Button 
            size="sm" 
            onClick={onDelete} 
            className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
            data-testid={`delete-btn-${event.event_id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Event Form Modal - Dark Premium Style
const EventFormModal = ({ event, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: event?.title || '',
    date: event?.date || '',
    time_start: event?.time_start || '18:00',
    time_end: event ? (event.time_end || '') : '22:00',
    location: event?.location || '',
    address: event?.address || '',
    description: event?.description || '',
    image_url: event?.image_url || '',
    max_participants: event?.max_participants || 100,
    is_free: event?.is_free ?? true,
    payment_link: event?.payment_link || '',
    is_published: event?.is_published ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await axios.post(`${API}/events/upload-image`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Use functional update to avoid stale closure
      setFormData(prev => ({ ...prev, image_url: res.data.image_url }));
      toast.success('Image téléchargée !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (event) {
        await axios.put(`${API}/events/${event.event_id}`, formData, { withCredentials: true });
        toast.success('Événement mis à jour !');
      } else {
        await axios.post(`${API}/events/`, formData, { withCredentials: true });
        toast.success('Événement créé !');
      }
      onSave();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-slate-900 p-6 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            {event ? 'Modifier l\'événement' : 'Créer un événement'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-500 hover:text-white hover:bg-slate-800">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Titre de l'événement *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Ex: Soirée de lancement du Club"
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500"
              required
              data-testid="event-title-input"
            />
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
                <Calendar className="w-4 h-4 text-orange-400" />
                Date *
              </label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white focus:border-orange-500"
                required
                data-testid="event-date-input"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
                <Clock className="w-4 h-4 text-orange-400" />
                Heure début *
              </label>
              <Input
                type="time"
                value={formData.time_start}
                onChange={(e) => setFormData({ ...formData, time_start: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white focus:border-orange-500"
                required
                data-testid="event-time-start-input"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
                <Clock className="w-4 h-4 text-orange-400" />
                Heure fin
              </label>
              <Input
                type="time"
                value={formData.time_end}
                onChange={(e) => setFormData({ ...formData, time_end: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white focus:border-orange-500"
                data-testid="event-time-end-input"
              />
            </div>
          </div>

          {/* Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
                <MapPin className="w-4 h-4 text-blue-400" />
                Lieu *
              </label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Ex: Sofitel Lyon Bellecour"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500"
                required
                data-testid="event-location-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Adresse complète *</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Ex: 20 Quai Gailleton, 69002 Lyon"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500"
                required
                data-testid="event-address-input"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Description *</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Décrivez votre événement..."
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500 min-h-[120px]"
              required
              data-testid="event-description-input"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
              <ImageIcon className="w-4 h-4 text-orange-400" />
              Image de l'événement
            </label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                data-testid="event-image-file-input"
              />
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/30"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full w-4 h-4 border-2 border-orange-500 border-t-transparent mr-2" />
                    Téléchargement...
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Choisir une image
                  </>
                )}
              </Button>
              {formData.image_url && (
                <div className="flex items-center gap-2">
                  <img src={resolveImageUrl(formData.image_url)} alt="Preview" className="w-16 h-16 rounded-xl object-cover border border-slate-700" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Capacity */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
              <Users className="w-4 h-4 text-slate-400" />
              Nombre de places disponibles
            </label>
            <Input
              type="number"
              min="1"
              max="10000"
              value={formData.max_participants}
              onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) })}
              className="bg-slate-800 border-slate-700 text-white focus:border-orange-500 w-32"
              data-testid="event-capacity-input"
            />
          </div>

          {/* Free/Paid */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">Tarification</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={formData.is_free}
                  onChange={() => setFormData({ ...formData, is_free: true, payment_link: '' })}
                  className="text-orange-500 focus:ring-orange-500 bg-slate-800 border-slate-600"
                  data-testid="event-free-radio"
                />
                <span className="text-slate-300">Gratuit</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!formData.is_free}
                  onChange={() => setFormData({ ...formData, is_free: false })}
                  className="text-orange-500 focus:ring-orange-500 bg-slate-800 border-slate-600"
                  data-testid="event-paid-radio"
                />
                <span className="text-slate-300">Payant</span>
              </label>
            </div>
            {!formData.is_free && (
              <div className="mt-3">
                <Input
                  value={formData.payment_link}
                  onChange={(e) => setFormData({ ...formData, payment_link: e.target.value })}
                  placeholder="Lien de paiement externe (ex: Stripe, HelloAsso...)"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500"
                  data-testid="event-payment-link-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  10M INVESTOR n'est pas responsable de l'organisation des événements de la communauté.
                </p>
              </div>
            )}
          </div>

          {/* Publish Status */}
          <div className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
            <input
              type="checkbox"
              checked={formData.is_published}
              onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
              className="w-5 h-5 text-orange-500 rounded bg-slate-700 border-slate-600 focus:ring-orange-500"
              data-testid="event-publish-checkbox"
            />
            <div>
              <p className="text-white font-medium">Publier l'événement</p>
              <p className="text-slate-500 text-sm">
                Si décoché, l'événement sera sauvegardé en brouillon.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button 
              type="button" 
              onClick={onClose} 
              className="flex-1 bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700"
              data-testid="cancel-event-btn"
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={saving} 
              className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white shadow-lg"
              data-testid="save-event-btn"
            >
              {saving ? 'Enregistrement...' : (event ? 'Mettre à jour' : 'Créer l\'événement')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Registrations Modal - Dark Style
const RegistrationsModal = ({ event, onClose }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    try {
      const res = await axios.get(`${API}/events/${event.event_id}/registrations`, { withCredentials: true });
      setRegistrations(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des inscriptions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-slate-800 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-400" />
              Inscriptions
            </h2>
            <p className="text-slate-500 text-sm">{event.title}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-500 hover:text-white hover:bg-slate-800">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-slate-700 border-t-orange-500 rounded-full animate-spin" />
            </div>
          ) : registrations.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-500">Aucune inscription pour le moment</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-slate-400 mb-4">{registrations.length} inscription(s)</p>
              {registrations.map((reg, index) => (
                <div key={reg.registration_id} className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white font-bold shadow-lg">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{reg.name}</p>
                    <p className="text-slate-500 text-sm">{reg.email}</p>
                    {reg.phone && <p className="text-slate-600 text-xs">{reg.phone}</p>}
                  </div>
                  <p className="text-slate-600 text-xs">
                    {new Date(reg.registered_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
